// Captures the browser install prompt as early as possible (module load) and keeps
// track of whether this PWA is already installed on the device.
import { Capacitor } from "@capacitor/core";

const INSTALLED_KEY = "yalola.pwaInstalled";
// Remembered install is trusted for 30 days; every launch of the installed PWA renews it,
// so an uninstalled app naturally "expires" and the install button comes back.
const INSTALLED_TTL_MS = 30 * 24 * 3600 * 1000;
let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((cb) => cb());

const readInstalled = () => {
  try {
    const raw = localStorage.getItem(INSTALLED_KEY);
    if (!raw) return false;
    if (raw === "1") return true; // legacy flag (no timestamp)
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < INSTALLED_TTL_MS;
  } catch { return false; }
};
const markInstalled = () => { try { localStorage.setItem(INSTALLED_KEY, String(Date.now())); } catch { /* noop */ } };

export const isStandalone = () =>
  // Inside the native Capacitor app the PWA is (better than) installed — never prompt.
  Capacitor.isNativePlatform()
  || (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
  || window.navigator.standalone === true;

// Chrome 140+ (desktop & Android) reports this very PWA through the manifest's
// related_applications {platform:"webapp"} entry. Older browsers return [] even when
// the app IS installed → only a positive answer is trusted.
const detectInstalledPwa = async () => {
  try {
    if (!window.navigator.getInstalledRelatedApps) return false;
    const apps = await window.navigator.getInstalledRelatedApps();
    return apps.some((a) => a.platform === "webapp");
  } catch { return false; }
};

if (typeof window !== "undefined") {
  // The installed PWA runs in standalone mode and shares localStorage with the browser
  // tabs of the same profile: each launch renews the "installed" memory, so a normal
  // yalola.app tab stops offering to install an app that is already on the machine.
  // (Chrome may still fire beforeinstallprompt for an installed PWA — seen in the field.)
  if (!Capacitor.isNativePlatform() && isStandalone()) markInstalled();
  window.addEventListener("beforeinstallprompt", async (e) => {
    e.preventDefault();
    if (await detectInstalledPwa()) { markInstalled(); deferredPrompt = null; notify(); return; }
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    markInstalled();
    deferredPrompt = null;
    notify();
  });
  detectInstalledPwa().then((yes) => { if (yes) { markInstalled(); deferredPrompt = null; notify(); } });
}

export const getInstallPrompt = () => deferredPrompt;
export const clearInstallPrompt = () => { deferredPrompt = null; notify(); };
export const onInstallChange = (cb) => { listeners.add(cb); return () => listeners.delete(cb); };
// Installed on this device (remembered from a standalone launch, appinstalled or
// getInstalledRelatedApps), even when the member is browsing yalola.app in a normal tab.
export const isPwaInstalled = () => readInstalled();
export const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent)
  // Modern iPads report a Mac userAgent — the touch screen gives them away.
  || (/macintosh/i.test(window.navigator.userAgent) && window.navigator.maxTouchPoints > 1);
export const isAndroid = () => /android/i.test(window.navigator.userAgent);
export const isDesktop = () => !isIOS() && !isAndroid() && !Capacitor.isNativePlatform();
// OS family of a desktop browser — key of the per-account memory (users.pwa_desktop).
export const desktopOs = () => {
  const ua = window.navigator.userAgent;
  if (/windows/i.test(ua)) return "windows";
  if (/cros/i.test(ua)) return "chromeos";
  if (/macintosh|mac os x/i.test(ua)) return "mac";
  if (/linux|x11/i.test(ua)) return "linux";
  return "other";
};
// The account opened the installed desktop PWA on this OS within 30 days — from ANY
// browser/profile of the machine (Chrome only sees its own profile, Edge/Chrome differ).
export const accountHasDesktopPwa = (user) => {
  if (!user || !isDesktop()) return false;
  const ts = user.pwa_desktop?.[desktopOs()];
  if (!ts) return false;
  const t = Date.parse(ts);
  return Number.isFinite(t) && Date.now() - t < INSTALLED_TTL_MS;
};
// Called once per standalone desktop launch (signed-in) → POST /users/me/pwa-launch.
export const shouldReportPwaLaunch = () =>
  !Capacitor.isNativePlatform() && isDesktop() && isStandalone();
